import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpIcon, XIcon } from 'lucide-react';
import { ProcessingTag } from '../ui/ProcessingTag';
import { useDb } from '../../hooks/useDb';
import type { Activity } from '../../types/stage';
import type { Processing } from '../../types/anna';
interface ListenOverlayProps {
  listening: boolean;
  interim: string;
  typing: boolean;
  note: string | null;
  activity: Activity;
  thinkingWhere: Processing;
  onSubmit: (text: string) => void;
  onCloseTyping: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;

/** Top of the screen: the owner's live transcript, the typing fallback and the "where does it run" tag. */
export function ListenOverlay({
  listening,
  interim,
  typing,
  note,
  activity,
  thinkingWhere,
  onSubmit,
  onCloseTyping
}: ListenOverlayProps) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestions = useDb(t => t.voiceSuggestions);
  useEffect(() => {
    if (typing) {
      setValue('');
      inputRef.current?.focus();
    }
  }, [typing]);
  const submit = (text: string) => {
    const t = text.trim();
    if (t) onSubmit(t);
  };
  return <div className="pointer-events-none absolute inset-x-0 top-36 z-30 flex flex-col items-center px-6 lg:top-40" data-id="element-1604" data-mp="2h01u0re9i97z">
      <AnimatePresence mode="wait" data-id="element-1605" data-mp="2lymsomdv8kub">
        {listening && !typing && <motion.p key="transcript" initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} className="max-w-[900px] text-center font-display text-[34px] font-extralight text-ink-soft lg:text-[40px]" aria-live="polite" data-id="element-1606" data-mp="2q1jhwvxwvf78">
            {interim || 'Ik luister'}
          </motion.p>}

        {typing && <motion.div key="typing" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} className="pointer-events-auto w-full max-w-[920px]" data-id="element-1607" data-mp="3dr3yj86le0dc">
            <form onSubmit={e => {
          e.preventDefault();
          submit(value);
        }} className="glass flex items-center gap-3 rounded-full py-2 pl-7 pr-2" data-id="element-1608" data-mp="1ngh7y465f388">
              <label htmlFor="ask-anna" className="sr-only" data-id="element-1609" data-mp="1xfq1wx10khul">Vraag Anna iets</label>
              <input id="ask-anna" ref={inputRef} value={value} onChange={e => setValue(e.target.value)} onKeyDown={e => e.key === 'Escape' && onCloseTyping()} placeholder="Vraag Anna iets…" autoComplete="off" className="min-w-0 flex-1 bg-transparent text-[26px] text-ink placeholder:text-ink-muted focus:outline-none" data-id="element-1610" data-mp="1se0lf1v0vbz4" />
              <button type="button" onClick={onCloseTyping} aria-label="Sluiten" className="grid h-14 w-14 shrink-0 place-items-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-white/10 hover:text-ink" data-id="element-1611" data-mp="3tzwcvydon25x">
                <XIcon className="h-6 w-6" data-id="element-1612" data-mp="3p8ebj97qyzkf" />
              </button>
              <button type="submit" aria-label="Versturen" disabled={!value.trim()} className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-accent text-night-950 transition-opacity duration-150 disabled:opacity-40" data-id="element-1613" data-mp="3acf5e44779hx">
                <ArrowUpIcon className="h-6 w-6" strokeWidth={2.25} data-id="element-1614" data-mp="36a1yhcdpt5jk" />
              </button>
            </form>
            {note && <p className="mt-3 text-center text-[22px] text-amber" data-id="element-1615" data-mp="zl5itxe9d30b">{note}</p>}
            <ul className="mt-4 flex flex-wrap justify-center gap-2" data-id="element-1616" data-mp="14jqle3gzfyrx">
              {suggestions.map(s => <li key={s} data-id="element-1617" data-mp="30mlk3nrnxtsk">
                  <button type="button" onClick={() => submit(s)} className="whitespace-nowrap rounded-full border border-white/10 bg-night-900 px-5 py-3 text-[22px] text-ink-soft transition-colors duration-150 hover:border-white/25 hover:text-ink" data-id="element-1618" data-mp="30ltoicy2fw2a">
                    {s}
                  </button>
                </li>)}
            </ul>
          </motion.div>}

        {activity === 'thinking' && !typing && !listening && <motion.div key="thinking" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="flex items-center gap-3 text-[22px] text-ink-muted" data-id="element-1619" data-mp="2cuxxvj5cj6wm">
            denkt <ProcessingTag where={thinkingWhere} size="wall" data-id="element-1620" data-mp="1yysw09obkox1" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}